import React from 'react'
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  SendOutlined,
  SwapOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons'
import {
  Alert,
  Button,
  Card,
  Empty,
  Flex,
  Select,
  Skeleton,
  Tooltip,
  Typography,
  theme,
} from 'antd'
import { Link } from 'react-router-dom'
import { path } from '../../../libs/path'
import { WalletGuardWallet, WalletGuardWalletFilters } from '../types'
import { WALLET_CURRENCIES } from '../wallets/config'
import { WGDashboardWalletCard } from './WGDashboardWalletCard'
import { getWalletStatus, walletStatuses, walletTypeLabels } from './wallets'
import styles from './wallets.module.css'

type FilterKey = 'currency' | 'type' | 'paymentSystems' | 'status'

type Props = {
  wallets: WalletGuardWallet[]
  total: number
  loading: boolean
  filters: WalletGuardWalletFilters
  hasActiveFilters: boolean
  paymentSystems: string[]
  onFilterChange: (key: FilterKey, values: string[]) => void
  onFiltersReset: () => void
  onWalletCheck: (wallet: WalletGuardWallet) => void
}

const typeIcons: Record<string, React.ReactNode> = {
  SENDER: <SendOutlined />,
  TRANSIT: <SwapOutlined />,
  FINAL: <CheckCircleOutlined />,
  OWN: <UserOutlined />,
}

export const WGDashboardWallets = ({
  wallets,
  total,
  loading,
  filters,
  hasActiveFilters,
  paymentSystems,
  onFilterChange,
  onFiltersReset,
  onWalletCheck,
}: Props) => {
  const { token } = theme.useToken()
  const allTypes = Array.from(
    new Set([
      ...Object.keys(walletTypeLabels),
      ...wallets.map(({ type }) => type),
    ]),
  )

  const filterFields: {
    key: FilterKey
    label: string
    placeholder: string
    options: { value: string; label: string }[]
  }[] = [
    {
      key: 'currency',
      label: 'Валюты',
      placeholder: 'Все валюты',
      options: WALLET_CURRENCIES.map((value) => ({ value, label: value })),
    },
    {
      key: 'type',
      label: 'Типы кошельков',
      placeholder: 'Все типы',
      options: allTypes.map((value) => ({
        value,
        label: walletTypeLabels[value] ?? value,
      })),
    },
    {
      key: 'paymentSystems',
      label: 'Платёжные системы',
      placeholder: 'Все ПС',
      options: paymentSystems.map((value) => ({ value, label: value })),
    },
    {
      key: 'status',
      label: 'Статусы кошельков',
      placeholder: 'Все статусы',
      options: Object.keys(walletStatuses).map((value) => ({
        value,
        label: getWalletStatus(value).label,
      })),
    },
  ]
  return (
    <>
      <Card
        aria-label="Схема движения средств"
        aria-busy={loading}
        style={{ minWidth: 0, boxShadow: token.boxShadowTertiary }}
      >
        <Flex justify="space-between" align="center" gap={16} wrap>
          <Typography.Title
            level={2}
            style={{
              margin: 0,
              fontSize: token.fontSizeSM,
              color: token.colorTextSecondary,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            Схема движения средств
          </Typography.Title>
          <Flex gap={8} wrap className={styles.filters}>
            {filterFields.map(({ key, label, ...filter }) => (
              <Select<string[]>
                key={key}
                {...filter}
                aria-label={label}
                className={styles.filter}
                size="small"
                mode="multiple"
                value={filters[key] ?? []}
                onChange={(values) => onFilterChange(key, values)}
                allowClear
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                notFoundContent="Нет вариантов"
              />
            ))}
            <Tooltip title="Сбросить фильтры">
              <Button
                size="small"
                icon={<CloseOutlined />}
                aria-label="Сбросить фильтры"
                disabled={!hasActiveFilters}
                onClick={onFiltersReset}
              />
            </Tooltip>
          </Flex>
        </Flex>

        {!loading && total > wallets.length && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 16 }}
            message={
              <>
                Показано {wallets.length} из {total}. Уточните фильтры или
                откройте{' '}
                <Link to={path.walletGuard.wallets()}>список кошельков</Link>.
              </>
            }
          />
        )}

        {!loading && wallets.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              hasActiveFilters
                ? 'Нет кошельков по выбранным фильтрам'
                : 'Кошельков пока нет'
            }
          />
        ) : (
          <div className={styles.flowScroll}>
            <div className={styles.flow}>
              {allTypes.map((type, index) => {
                const groupWallets = wallets.filter(
                  (wallet) => wallet.type === type,
                )
                const label = walletTypeLabels[type] ?? type

                return (
                  <section
                    key={type}
                    aria-label={label}
                    className={styles.group}
                  >
                    <Flex
                      align="center"
                      justify="center"
                      gap={6}
                      className={styles.groupHeading}
                      style={{ borderColor: token.colorBorderSecondary }}
                    >
                      <Typography.Text type="secondary">
                        {typeIcons[type] ?? <WalletOutlined />}
                      </Typography.Text>
                      <Typography.Title
                        level={3}
                        style={{
                          margin: 0,
                          color: token.colorTextSecondary,
                          fontSize: token.fontSizeSM,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {label}
                      </Typography.Title>
                    </Flex>

                    <Flex
                      vertical
                      gap={12}
                      className={styles.walletList}
                      role="region"
                      aria-label={`${label}: список кошельков`}
                      tabIndex={
                        !loading && groupWallets.length > 0 ? 0 : undefined
                      }
                    >
                      {loading ? (
                        <Card size="small">
                          <Skeleton active title paragraph={{ rows: 3 }} />
                        </Card>
                      ) : groupWallets.length > 0 ? (
                        groupWallets.map((wallet) => (
                          <WGDashboardWalletCard
                            key={wallet.id}
                            wallet={wallet}
                            onCheck={onWalletCheck}
                          />
                        ))
                      ) : (
                        <Typography.Text
                          type="secondary"
                          className={styles.emptyGroup}
                        >
                          Нет кошельков
                        </Typography.Text>
                      )}
                    </Flex>

                    {index < allTypes.length - 1 && (
                      <div
                        aria-hidden="true"
                        className={styles.connector}
                        style={{ color: token.colorPrimary }}
                      >
                        <span />
                        <ArrowRightOutlined />
                        <span />
                      </div>
                    )}
                  </section>
                )
              })}
            </div>
          </div>
        )}
      </Card>
    </>
  )
}
